import { ApiService, UploadedImage } from '../services/api';
import { EventBus } from '../utils/eventBus';
import { createElement, createButton, clearElement } from '../utils/dom';

export class ImageGalleryModal {
  private modal: HTMLElement | null = null;
  private imagesContainer: HTMLElement | null = null;
  private images: UploadedImage[] = [];
  private cursor: string | null = null;
  private hasMore: boolean = false;
  private isLoading: boolean = false;

  constructor(
    private eventBus: EventBus,
    private apiService: ApiService
  ) {}

  async show(): Promise<void> {
    this.createModal();
    await this.loadImages();
  }

  private createModal(): void {
    // Create modal overlay
    this.modal = createElement('div', 'modal-overlay');

    // Create modal content
    const modalContent = createElement('div', 'modal-content image-gallery-modal');

    // Header
    const header = createElement('div', 'modal-header');
    const title = createElement('h2', 'modal-title');
    title.textContent = 'Select Image';
    header.appendChild(title);

    const closeButton = createButton('✕', 'btn-close-modal', () => this.close());
    header.appendChild(closeButton);
    modalContent.appendChild(header);

    // Images container
    this.imagesContainer = createElement('div', 'images-grid');
    modalContent.appendChild(this.imagesContainer);

    // Footer with actions
    const footer = createElement('div', 'modal-footer');

    const deleteAllButton = createButton('Delete All Images', 'btn btn-danger', async () => {
      if (confirm('Are you sure you want to delete all uploaded images? This cannot be undone.')) {
        await this.deleteAllImages();
      }
    });
    footer.appendChild(deleteAllButton);

    const navButtons = createElement('div', 'modal-nav-buttons');

    const prevButton = createButton('← Previous', 'btn btn-secondary', () => {
      // Note: R2 doesn't support backward pagination, so we'll reload from start
      this.cursor = null;
      this.loadImages();
    });
    prevButton.disabled = true; // Disable for now as backward pagination is complex
    navButtons.appendChild(prevButton);

    const nextButton = createButton('Next →', 'btn btn-secondary', async () => {
      if (this.hasMore && this.cursor) {
        await this.loadImages(this.cursor);
      }
    });
    nextButton.id = 'next-button';
    navButtons.appendChild(nextButton);

    footer.appendChild(navButtons);
    modalContent.appendChild(footer);

    this.modal.appendChild(modalContent);
    document.body.appendChild(this.modal);

    // Close on overlay click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.close();
      }
    });
  }

  private async loadImages(cursor?: string): Promise<void> {
    if (this.isLoading || !this.imagesContainer) return;

    this.isLoading = true;
    this.renderLoading();

    try {
      const response = await this.apiService.listImages(cursor);
      this.images = response.images;
      this.cursor = response.cursor;
      this.hasMore = response.hasMore;

      this.renderImages();
      this.updateNavButtons();
    } catch (error) {
      console.error('Failed to load images:', error);
      this.renderError();
    } finally {
      this.isLoading = false;
    }
  }

  private renderLoading(): void {
    if (!this.imagesContainer) return;
    clearElement(this.imagesContainer);
    const loading = createElement('div', 'loading-message');
    loading.textContent = 'Loading images...';
    this.imagesContainer.appendChild(loading);
  }

  private renderError(): void {
    if (!this.imagesContainer) return;
    clearElement(this.imagesContainer);
    const error = createElement('div', 'error-message');
    error.textContent = 'Failed to load images. Please try again.';
    this.imagesContainer.appendChild(error);
  }

  private renderImages(): void {
    if (!this.imagesContainer) return;
    clearElement(this.imagesContainer);

    if (this.images.length === 0) {
      const empty = createElement('div', 'empty-message');
      empty.textContent = 'No images uploaded yet.';
      this.imagesContainer.appendChild(empty);
      return;
    }

    this.images.forEach(image => {
      const imageCard = createElement('div', 'image-card');
      imageCard.addEventListener('click', () => {
        this.eventBus.emit('image:selected', image.url);
        this.close();
      });

      const img = document.createElement('img');
      img.src = image.url;
      img.alt = image.key;
      img.loading = 'lazy';
      imageCard.appendChild(img);

      const info = createElement('div', 'image-info');
      const date = new Date(image.uploaded);
      info.textContent = date.toLocaleDateString();
      imageCard.appendChild(info);

      this.imagesContainer!.appendChild(imageCard);
    });
  }

  private updateNavButtons(): void {
    const nextButton = document.getElementById('next-button') as HTMLButtonElement;
    if (nextButton) {
      nextButton.disabled = !this.hasMore;
    }
  }

  private async deleteAllImages(): Promise<void> {
    if (!this.imagesContainer) return;

    this.isLoading = true;
    this.renderLoading();

    try {
      const result = await this.apiService.deleteAllImages();
      alert(result.message);

      // Reload images after deletion
      this.cursor = null;
      await this.loadImages();
    } catch (error) {
      console.error('Failed to delete images:', error);
      alert('Failed to delete images. Please try again.');
      this.renderImages();
    } finally {
      this.isLoading = false;
    }
  }

  private close(): void {
    if (this.modal) {
      document.body.removeChild(this.modal);
      this.modal = null;
    }
  }
}
